import React from "react"
import { useParams, useNavigate } from "react-router-dom"
import logo from "../images/horizontal-light.svg"
import MobileHomeIcon from "../icons/MobileHomeIcon.png"
import ProfileMenu from "./ProfileMenu"
import { DatasetSchemas, Datasets, Schemas } from "../lib/store"
import {
  Accordion,
  AccordionButton,
  AccordionItem,
  AccordionPanel,
  Box,
  Flex,
  Image,
  useBreakpointValue,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Button,
} from "@chakra-ui/react"
import Link from "./Link"
import { ChevronDownIcon } from "@chakra-ui/icons"

export default function Sidebar() {
  const [datasetSchemas] = DatasetSchemas.useState()
  const [datasets] = Datasets.useState()
  const [schemas] = Schemas.useState()
  const { datasetId, schemaId } = useParams()
  const navigate = useNavigate()

  const logoSrc = useBreakpointValue({
    base: MobileHomeIcon,
    md: logo,
  })

  const isMobile = useBreakpointValue({ base: true, md: false })

  const selectedDataset = datasets[datasetId]
  const selectedSchema = schemas[schemaId]

  const selectedDatasetSchemas = React.useMemo(() => {
    // If we are viewing all datasets, don't render schemas
    if (!datasetId) return <></>
    // If we have no dataset selected, don't render schemas
    const selected = datasetSchemas[datasetId]
    if (!selected) return <></>
    // Loop over the selected dataset:schemas mapping creating our entries
    return selected.map((id) => {
      let schema = schemas[id]
      return (
        <Box key={id}>
          <Link to={`/datasets/${datasetId}/schemas/${id}/records`}>
            {schema.name}
          </Link>
        </Box>
      )
    })
  }, [schemas, datasetId, schemaId, datasetSchemas])

  const mobileMenus = (
    <Flex
      gap={2}
      justifyContent="flex-start"
      alignItems="center"
      mr={4}
      minWidth="0"
      overflow="hidden"
    >
      <Menu minWidth="0">
        <MenuButton
          as={Button}
          rightIcon={<ChevronDownIcon />}
          minWidth="0"
          size="sm"
        >
          <Box
            textOverflow="ellipsis"
            overflow="hidden"
            whiteSpace="nowrap"
            pr={2}
          >
            {selectedDataset?.name || "Dataset"}
          </Box>
        </MenuButton>
        <MenuList>
          {Object.values(datasets).map((dataset) => (
            <MenuItem
              key={dataset.id}
              onClick={() => navigate(`/datasets/${dataset.id}`)}
            >
              {dataset.name}
            </MenuItem>
          ))}
        </MenuList>
      </Menu>
      <Menu minWidth="0">
        <MenuButton
          size="sm"
          as={Button}
          rightIcon={<ChevronDownIcon />}
          isDisabled={!datasetId}
          minWidth="0"
        >
          <Box
            textOverflow="ellipsis"
            overflow="hidden"
            whiteSpace="nowrap"
            pr={2}
          >
            {selectedSchema?.name || "Schema"}
          </Box>
        </MenuButton>
        <MenuList>
          {!datasetId ? (
            <MenuItem isDisabled>Please select dataset first</MenuItem>
          ) : (
            datasetSchemas[datasetId]?.map((id) => (
              <MenuItem
                key={id}
                onClick={() =>
                  navigate(`/datasets/${datasetId}/schemas/${id}/records`)
                }
              >
                {schemas[id].name}
              </MenuItem>
            ))
          )}
        </MenuList>
      </Menu>
    </Flex>
  )

  return (
    <Flex
      flexDirection={{ base: "row", md: "column" }}
      height={{ base: "4em", md: "100vh" }}
      width={{ base: "100%", md: "14em" }}
      alignItems={{ base: "center", md: "stretch" }}
      bg="Primary.700"
      overflowY="scroll"
      css={{
        "&::-webkit-scrollbar": {
          display: "none",
        },
      }}
    >
      <Box flexShrink={0} scrollbar="hidden">
        <Link to="/" bg="Primary.800">
          <Image
            width={{ base: "3em", md: "80%" }}
            margin="auto"
            mt={{ base: "auto", md: "15px" }}
            mb={{ base: "auto", md: "15px" }}
            ml={{ base: "1em", md: "auto" }}
            mr={{ base: "1em", md: "auto" }}
            src={logoSrc}
            alt="Blind Insight Logo"
            display="block"
          />
        </Link>
      </Box>

      {isMobile ? (
        mobileMenus
      ) : (
        <Accordion
          variant="dataSets"
          padding={0}
          defaultIndex={
            datasetId
              ? [Object.keys(datasets).findIndex((id) => id === datasetId)]
              : []
          }
          index={
            datasetId
              ? [Object.keys(datasets).findIndex((id) => id === datasetId)]
              : []
          }
        >
          {Object.values(datasets).map((dataset) => (
            <AccordionItem key={dataset.id}>
              <AccordionButton
                onClick={() => navigate(`/datasets/${dataset.id}`)}
              >
                {dataset.name}
              </AccordionButton>
              <AccordionPanel>
                {dataset.id === datasetId ? selectedDatasetSchemas : null}
              </AccordionPanel>
            </AccordionItem>
          ))}
        </Accordion>
      )}
      <ProfileMenu />
    </Flex>
  )
}
